import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './Gallery.css';
import photo1 from '../photos/1.jpeg';
import photo2 from '../photos/2.jpeg';
import photo3 from '../photos/3.jpeg';
import photo4 from '../photos/4.jpeg';
import photo5 from '../photos/5.jpeg';
import photo6 from '../photos/6.jpeg';
import photo7 from '../photos/7.jpeg';

const photos = [
    { src: photo2, alt: 'M & P' },
    { src: photo1, alt: 'M & P' },
    { src: photo3, alt: 'M & P' },
    { src: photo5, alt: 'M & P' },
    { src: photo4, alt: 'M & P' },
    { src: photo7, alt: 'M & P' },
    { src: photo6, alt: 'M & P' },
];

// Extend photos for infinite loop: [Set 1] [Set 2] [Set 3]
const extendedPhotos = [...photos, ...photos, ...photos];
const CARD_WIDTH = 500;
const CARD_MARGIN = 40;
const ITEM_WIDTH = CARD_WIDTH + (CARD_MARGIN * 2);

const Gallery = () => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState(photos.length); // Start in the middle set
    const [isTransitioning, setIsTransitioning] = useState(true);
    const trackRef = useRef(null);

    const handleNext = useCallback(() => {
        setActiveIndex((prev) => prev + 1);
    }, []);

    const handlePrev = useCallback(() => {
        setActiveIndex((prev) => prev - 1);
    }, []);

    // Handle seamless loop (Silent Jump)
    useEffect(() => {
        const jumpTiming = 800; // Duration of transition in CSS

        if (activeIndex >= photos.length * 2) {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
                setActiveIndex(photos.length);
            }, jumpTiming);
            return () => clearTimeout(timer);
        } else if (activeIndex < photos.length) {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
                setActiveIndex(photos.length * 2 - 1);
            }, jumpTiming);
            return () => clearTimeout(timer);
        }
    }, [activeIndex]);

    // Re-enable transition after jumps
    useEffect(() => {
        if (!isTransitioning) {
            const raf = requestAnimationFrame(() => {
                const raf2 = requestAnimationFrame(() => {
                    setIsTransitioning(true);
                });
            });
            return () => cancelAnimationFrame(raf);
        }
    }, [isTransitioning]);

    const goToSlide = (originalIndex) => {
        setIsTransitioning(true);
        setActiveIndex(photos.length + originalIndex);
    };

    // Auto play
    useEffect(() => {
        const interval = setInterval(handleNext, 6000);
        return () => clearInterval(interval);
    }, [handleNext]);

    const realIndex = activeIndex % photos.length;

    return (
        <section id="photo-gallery" className="gallery-section-reset">
            <h2 className="animate-fadeInUp">{t('our_moments')}</h2>
            <p className="animate-fadeIn">{t('gallery_text')}</p>

            {/* Premium Desktop Carousel */}
            <div className="desktop-gallery-wrapper v2-reboot">
                <div className="carousel-container">
                    <button className="nav-button prev" onClick={handlePrev} aria-label="Previous">
                        <svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" /></svg>
                    </button>

                    <div className="carousel-viewport">
                        <div
                            ref={trackRef}
                            className={`carousel-track ${!isTransitioning ? 'no-transition' : ''}`}
                            style={{
                                transform: `translateX(-${activeIndex * ITEM_WIDTH + ITEM_WIDTH / 2}px)`
                            }}
                        >
                            {extendedPhotos.map((p, index) => (
                                <div
                                    className={`gallery-card ${index === activeIndex ? 'active' : ''}`}
                                    key={index}
                                    onClick={() => goToSlide(index % photos.length)}
                                >
                                    <div className="card-inner">
                                        <img src={p.src} alt={p.alt} loading="lazy" />
                                    </div>
                                    <div className="card-overlay" />
                                </div>
                            ))}
                        </div>
                    </div>

                    <button className="nav-button next" onClick={handleNext} aria-label="Next">
                        <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                    </button>
                </div>

                <div className="carousel-dots">
                    {photos.map((_, index) => (
                        <div
                            key={index}
                            className={`dot ${index === realIndex ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                        />
                    ))}
                </div>
            </div>

            {/* Mobile Carousel */}
            <div className="mobile-gallery">
                <Carousel showThumbs={false} infiniteLoop autoPlay showStatus={false}>
                    {photos.map((p, index) => (
                        <div key={index} className="carousel-slide-container">
                            <img src={p.src} alt={p.alt} />
                        </div>
                    ))}
                </Carousel>
            </div>
        </section>
    );
};

export default Gallery;

